import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Settings, RefreshCw, Loader2, CheckCircle, AlertTriangle, Filter, ShieldCheck, Radio, Slack, Trash2 } from "lucide-react";
import { EMPTY_RED_DOT_ROSTER, RED_DOT_MAX_RESPONDERS, type RedDotRoster } from "@shared/red-dot";
import { rosterAdd, rosterSetActive, rosterRemove } from "@/lib/red-dot";
import type { LiveIngestMode } from "@shared/live-ingest";
import { describeLiveIngest, fullUntilFromDuration } from "@/lib/live-ingest-helpers";
// SPEC-AdminImpersonation §"Client wiring #5" — admin-only launcher for
// "View as Agent / View as Senior". Gates internally.
import { ImpersonationLauncher } from "@/components/admin/impersonation-launcher";

type BackfillResult = {
  total?: number;
  candidates?: number;
  backfilled?: number;
  errors?: number;
};

function BackfillCard({
  title,
  description,
  endpoint,
  testId,
}: {
  title: string;
  description: string;
  endpoint: string;
  testId: string;
}) {
  const { toast } = useToast();
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<BackfillResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setIsRunning(true);
    setResult(null);
    setError(null);
    try {
      const res = await apiRequest("POST", endpoint);
      const data = await res.json();
      setResult(data);
      toast({
        title: "Backfill complete",
        description: `Updated ${data.backfilled ?? 0} deliveries${data.errors ? `, ${data.errors} errors` : ""}`,
      });
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setError(msg);
      toast({ title: "Backfill failed", description: msg, variant: "destructive" });
    } finally {
      setIsRunning(false);
    }
  };

  return (
    <Card data-testid={testId}>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button
          onClick={run}
          disabled={isRunning}
          data-testid={`${testId}-button`}
        >
          {isRunning ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          {isRunning ? "Running..." : "Run Backfill"}
        </Button>

        {result && (
          <div className="flex items-center gap-2 flex-wrap text-sm" data-testid={`${testId}-result`}>
            <CheckCircle className="h-4 w-4 text-green-500" />
            <span>Total: {result.total ?? "—"}</span>
            {result.candidates != null && <span>Candidates: {result.candidates}</span>}
            <Badge variant="default">{result.backfilled ?? 0} updated</Badge>
            {(result.errors ?? 0) > 0 && (
              <Badge variant="destructive">{result.errors} errors</Badge>
            )}
          </div>
        )}

        {error && (
          <div className="flex items-center gap-2 text-sm text-destructive" data-testid={`${testId}-error`}>
            <AlertTriangle className="h-4 w-4" />
            <span>{error}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const QATT1_STORAGE_KEY = "starling_qatt1_filter";

export function useQatt1Filter() {
  const [enabled, setEnabled] = useState(() => {
    try {
      return localStorage.getItem(QATT1_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });

  const toggle = (value: boolean) => {
    setEnabled(value);
    try {
      localStorage.setItem(QATT1_STORAGE_KEY, value ? "true" : "false");
    } catch {}
    window.dispatchEvent(new Event("qatt1-filter-changed"));
  };

  useEffect(() => {
    const handler = () => {
      try {
        setEnabled(localStorage.getItem(QATT1_STORAGE_KEY) === "true");
      } catch {}
    };
    window.addEventListener("qatt1-filter-changed", handler);
    window.addEventListener("storage", handler);
    return () => {
      window.removeEventListener("qatt1-filter-changed", handler);
      window.removeEventListener("storage", handler);
    };
  }, []);

  return { enabled, toggle };
}

type LiveIngestState = {
  enabled: boolean;
  mode: LiveIngestMode;
  fullUntil: string | null;
  effectiveMode: LiveIngestMode;
  updatedAt: string | null;
  updatedBy: string | null;
};

type AdminSettingsResponse = {
  agentsSkipSeniorDecided: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
  liveIngest?: LiveIngestState;
  redDotRoster?: RedDotRoster;
};

/**
 * "Agent double-check" — the QAM-wide, DB-persisted Agent exclusion toggle.
 *
 * Unlike the Demo Filters switch above (localStorage, per-browser), this one
 * must apply to every user, so it round-trips through
 * /api/admin/settings and is only readable/writable by an Admin.
 */
function AgentSkipSeniorDecidedCard() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<AdminSettingsResponse>({
    queryKey: ["/api/admin/settings"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/admin/settings");
      return res.json();
    },
  });

  const mutation = useMutation({
    mutationFn: async (enabled: boolean) => {
      const res = await apiRequest(
        "PUT",
        "/api/admin/settings/agents-skip-senior-decided",
        { enabled },
      );
      return res.json() as Promise<AdminSettingsResponse>;
    },
    onSuccess: (next) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/settings"] });
      toast({
        title: next.agentsSkipSeniorDecided ? "Setting on" : "Setting off",
        description: next.agentsSkipSeniorDecided
          ? "Agents will skip deliveries a Senior has already decided."
          : "Agents will see Senior-decided deliveries again.",
      });
    },
    onError: (e) => {
      toast({
        title: "Couldn't save setting",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    },
  });

  const busy = isLoading || mutation.isPending;

  return (
    <Card data-testid="card-agent-skip-senior-decided">
      <CardHeader>
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Agent double-check</CardTitle>
        </div>
        <CardDescription>Controls which deliveries reach the Agent QA queue</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between gap-4">
          <div className="space-y-0.5">
            <Label
              htmlFor="agent-skip-senior-decided-toggle"
              className="text-sm font-medium"
              data-testid="label-agent-skip-senior-decided"
            >
              Agents skip Senior-decided deliveries
            </Label>
            <p className="text-xs text-muted-foreground">
              When on, Agents are not served deliveries a Senior has already decided,
              including Senior bads still being resolved. Applies to everyone until
              turned off.
            </p>
          </div>
          <div className="flex items-center gap-2">
            {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            <Switch
              id="agent-skip-senior-decided-toggle"
              checked={data?.agentsSkipSeniorDecided ?? false}
              disabled={busy}
              onCheckedChange={(v) => mutation.mutate(v)}
              data-testid="switch-agent-skip-senior-decided"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * SPEC-RedDotOps §4 — "Red dot responders": who gets tagged in the ops Slack
 * channel when someone sends a Bad delivery there. One global list (not per
 * user), Admin-only. Every change saves straight away by PUTting the whole
 * list. Staging shares the production database, so editing here on staging
 * edits the production list.
 */
function RedDotRosterCard() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [name, setName] = useState("");
  const [slackUserId, setSlackUserId] = useState("");

  const { data, isLoading } = useQuery<AdminSettingsResponse>({
    queryKey: ["/api/admin/settings"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/admin/settings");
      return res.json();
    },
  });
  const roster = data?.redDotRoster ?? EMPTY_RED_DOT_ROSTER;

  const mutation = useMutation({
    mutationFn: async (next: RedDotRoster) => {
      const res = await apiRequest("PUT", "/api/admin/settings/red-dot-roster", next);
      return res.json() as Promise<AdminSettingsResponse>;
    },
    onSuccess: (next) => {
      queryClient.setQueryData(["/api/admin/settings"], next);
      queryClient.invalidateQueries({ queryKey: ["/api/qa/red-dot/recipients"] });
    },
    onError: (e) => {
      toast({
        title: "Couldn't save the list",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    },
  });

  const busy = isLoading || mutation.isPending;

  const add = () => {
    const next = rosterAdd(roster, name, slackUserId.toUpperCase());
    if (!next.ok) {
      toast({ title: "Can't add that person", description: next.error, variant: "destructive" });
      return;
    }
    mutation.mutate(next.roster, { onSuccess: () => { setName(""); setSlackUserId(""); } });
  };

  const onCount = roster.responders.filter((r) => r.active).length;

  return (
    <Card data-testid="card-red-dot-roster">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Slack className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Red dot responders</CardTitle>
          {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>
        <CardDescription>
          The people tagged in the ops Slack channel when someone sends a Bad delivery to #ops.
          Switch on whoever is on duty today. Applies to everyone.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {roster.responders.length === 0 ? (
          <p className="text-sm text-muted-foreground" data-testid="red-dot-empty">
            No one is on the list yet. Messages will still post, but nobody will be tagged.
          </p>
        ) : (
          <div className="space-y-2">
            {roster.responders.map((r) => (
              <div
                key={r.slackUserId}
                className="flex items-center gap-3 rounded-md border px-3 py-2"
                data-testid={`red-dot-row-${r.slackUserId}`}
              >
                <span className="text-sm font-medium flex-1 min-w-0 truncate">{r.name}</span>
                <code className="text-xs text-muted-foreground">{r.slackUserId}</code>
                <Switch
                  checked={r.active}
                  disabled={busy}
                  onCheckedChange={(v) => mutation.mutate(rosterSetActive(roster, r.slackUserId, v))}
                  aria-label={`Tag ${r.name}`}
                  data-testid={`switch-red-dot-${r.slackUserId}`}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={busy}
                  onClick={() => mutation.mutate(rosterRemove(roster, r.slackUserId))}
                  aria-label={`Remove ${r.name}`}
                  data-testid={`btn-red-dot-remove-${r.slackUserId}`}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
            <p className="text-xs text-muted-foreground" data-testid="red-dot-on-count">
              {onCount === 0 ? "Nobody is switched on — messages will post without tags." : `${onCount} switched on.`}
            </p>
          </div>
        )}

        <div className="flex items-end gap-2 pt-1">
          <div className="flex-1 space-y-1">
            <Label htmlFor="red-dot-name" className="text-xs">Name</Label>
            <Input
              id="red-dot-name"
              value={name}
              maxLength={40}
              onChange={(e) => setName(e.target.value)}
              placeholder="Joseph"
              data-testid="input-red-dot-name"
            />
          </div>
          <div className="flex-1 space-y-1">
            <Label htmlFor="red-dot-slack-id" className="text-xs">Slack member ID</Label>
            <Input
              id="red-dot-slack-id"
              value={slackUserId}
              onChange={(e) => setSlackUserId(e.target.value.trim())}
              onKeyDown={(e) => { if (e.key === "Enter") add(); }}
              placeholder="U0123ABC"
              data-testid="input-red-dot-slack-id"
            />
          </div>
          <Button
            onClick={add}
            disabled={busy || !name.trim() || !slackUserId || roster.responders.length >= RED_DOT_MAX_RESPONDERS}
            data-testid="btn-red-dot-add"
          >
            Add person
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          To find a member ID: in Slack, open the person's profile, click the three-dot "More" button,
          then "Copy member ID". It starts with U.
        </p>
      </CardContent>
    </Card>
  );
}

const LIVE_INGEST_DURATIONS_H = [1, 2, 4, 8] as const;

/**
 * SPEC-staging-hardening §5.10 — "Live ingest": what this deployment does with
 * live Onfleet traffic. Only exists where the server sets LIVE_INGEST_SWITCH
 * (staging); on Replit production the API reports enabled:false and the card
 * is not rendered. Off and Ingest apply on click; Full asks for an expiry.
 */
function LiveIngestCard() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [armingFull, setArmingFull] = useState(false);
  const [durationH, setDurationH] = useState<string>("1");
  const [now, setNow] = useState(() => Date.now());

  const { data, isLoading } = useQuery<AdminSettingsResponse>({
    queryKey: ["/api/admin/settings"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/admin/settings");
      return res.json();
    },
    // Keeps the expiry countdown honest and picks up a flip from another tab.
    refetchInterval: 30_000,
  });

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  const mutation = useMutation({
    mutationFn: async (body: { mode: LiveIngestMode; fullUntil?: string }) => {
      const res = await apiRequest("PUT", "/api/admin/settings/live-ingest", body);
      return res.json() as Promise<AdminSettingsResponse>;
    },
    onSuccess: (next) => {
      setArmingFull(false);
      setNow(Date.now());
      queryClient.setQueryData(["/api/admin/settings"], next);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/settings"] });
      const li = next.liveIngest;
      toast({
        title: "Live ingest updated",
        description: li ? describeLiveIngest(li, Date.now()) : undefined,
      });
    },
    onError: (e) => {
      toast({
        title: "Couldn't change live ingest",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    },
  });

  if (!data?.liveIngest?.enabled) return null;
  const li = data.liveIngest;

  const busy = isLoading || mutation.isPending;
  const fullActive = li.mode === "full" && li.effectiveMode === "full" && !!li.fullUntil && Date.parse(li.fullUntil) > now;
  const selected: LiveIngestMode = armingFull ? "full" : li.mode;
  const hours = Number(durationH);

  const segment = (mode: LiveIngestMode, label: string, testId: string) => (
    <Button
      type="button"
      size="sm"
      variant={selected === mode ? "default" : "outline"}
      disabled={busy}
      data-testid={testId}
      onClick={() => {
        if (mode === "full") {
          setArmingFull(true);
          return;
        }
        setArmingFull(false);
        mutation.mutate({ mode });
      }}
    >
      {label}
    </Button>
  );

  return (
    <Card data-testid="card-live-ingest">
      <CardHeader>
        <div className="flex items-center gap-2">
          <Radio className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base">Live ingest</CardTitle>
        </div>
        <CardDescription>Controls what this deployment does with live Onfleet traffic</CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="inline-flex gap-1" role="group" aria-label="Live ingest mode">
            {segment("off", "Off", "button-live-ingest-off")}
            {segment("ingest", "Ingest", "button-live-ingest-ingest")}
            {segment("full", "Full", "button-live-ingest-full")}
          </div>
          {busy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
        </div>

        {armingFull && (
          <div className="flex items-center gap-2 flex-wrap">
            <Select value={durationH} onValueChange={setDurationH} disabled={busy}>
              <SelectTrigger className="w-24" data-testid="select-live-ingest-duration">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LIVE_INGEST_DURATIONS_H.map((h) => (
                  <SelectItem key={h} value={String(h)}>{h} h</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              type="button"
              size="sm"
              disabled={busy}
              data-testid="button-live-ingest-start-full"
              onClick={() => mutation.mutate({ mode: "full", fullUntil: fullUntilFromDuration(hours, Date.now()) })}
            >
              {fullActive ? `Restart full mode for ${hours} h` : `Start full mode for ${hours} h`}
            </Button>
            <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={() => setArmingFull(false)}>
              Cancel
            </Button>
          </div>
        )}

        <p className="text-sm text-muted-foreground" data-testid="text-live-ingest-status">
          {describeLiveIngest(li, now)}
        </p>
        <p className="text-xs text-muted-foreground">
          Full runs the OpenAI extraction pipeline and the polling loops on live volume, so it
          always switches back to Ingest after the window you pick. Re-extract on a delivery
          works in every mode.
        </p>
      </CardContent>
    </Card>
  );
}

export default function AdminSettings() {
  const { enabled: qatt1Enabled, toggle: toggleQatt1 } = useQatt1Filter();

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Settings className="h-6 w-6 text-muted-foreground" />
        <div>
          <h1 className="text-xl font-semibold" data-testid="text-admin-settings-title">Admin Settings</h1>
          <p className="text-sm text-muted-foreground">System maintenance and data backfill tools</p>
        </div>
      </div>

      <ImpersonationLauncher />

      <AgentSkipSeniorDecidedCard />
      <LiveIngestCard />
      <RedDotRosterCard />

      <Card data-testid="card-demo-filters">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-base">Demo Filters</CardTitle>
          </div>
          <CardDescription>Toggle task type filters for demo purposes</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between gap-4">
            <div className="space-y-0.5">
              <Label htmlFor="qatt1-toggle" className="text-sm font-medium" data-testid="label-qatt1-toggle">
                Connect Only (QATT_1)
              </Label>
              <p className="text-xs text-muted-foreground">
                When enabled, only Connect (QATT_1) tasks are shown in the QA list
              </p>
            </div>
            <Switch
              id="qatt1-toggle"
              checked={qatt1Enabled}
              onCheckedChange={toggleQatt1}
              data-testid="switch-qatt1-filter"
            />
          </div>
        </CardContent>
      </Card>

      <BackfillCard
        title="Backfill Actual Extraction"
        description="Re-runs actual extraction (recipient, drop zone, etc.) for all deliveries stuck in 'pending' status. This populates the Actual (POD) column in the Expected vs Actual comparison."
        endpoint="/api/qa/backfill-actual"
        testId="backfill-actual"
      />

      <BackfillCard
        title="Backfill Expected Extraction"
        description="Re-runs expected extraction (recipient, street number, unit, address type, drop zone) from task data for deliveries missing expected fields."
        endpoint="/api/qa/backfill-expected"
        testId="backfill-expected"
      />
    </div>
  );
}
